import React, { useState } from "react";
import { LegalSheet } from "./LegalSheet";

// ─────────────────────────────────────────────
// ZENITH DESIGN TOKENS
// ─────────────────────────────────────────────
const Z = {
  primary:        "#55624d",
  primaryFixed:   "#d9e7cd",
  primaryFixedDim:"#bdcbb2",
  onPrimary:      "#ffffff",
  surface:        "#f8faf3",
  surfaceContainer: "#ecefe8",
  surfaceContainerLow: "#f2f4ed",
  surfaceContainerLowest: "#ffffff",
  onSurface:      "#191c18",
  onSurfaceVariant: "#444841",
  secondary:      "#755754",
  secondaryContainer: "#fed7d2",
  onSecondaryContainer: "#795b58",
  outline:        "#757870",
  outlineVariant: "#c5c8be",
  error:          "#ba1a1a",
  errorContainer: "#ffdad6",
};

const inputStyle = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: 12,
  border: `1px solid ${Z.outlineVariant}`,
  background: Z.surfaceContainerLowest,
  fontSize: 15,
  color: Z.onSurface,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

const primaryBtn = {
  width: "100%",
  padding: "14px 20px",
  borderRadius: 12,
  border: "none",
  background: Z.primary,
  color: Z.onPrimary,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  letterSpacing: "0.01em",
};

const outlineBtn = {
  width: "100%",
  padding: "14px 20px",
  borderRadius: 12,
  border: `1px solid ${Z.outlineVariant}`,
  background: Z.surfaceContainerLowest,
  fontSize: 15,
  fontWeight: 500,
  color: Z.onSurface,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
};

const linkBtn = {
  background: "none", border: "none",
  color: Z.primary, fontSize: 13,
  cursor: "pointer",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontWeight: 500, padding: 0,
};

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function mapError(msg) {
  if (!msg) return "Ein unbekannter Fehler ist aufgetreten.";
  if (msg.includes("Invalid login credentials")) return "E-Mail oder Passwort ist falsch.";
  if (msg.includes("Email not confirmed"))       return "Bitte bestätige zuerst deine E-Mail-Adresse.";
  if (msg.includes("User already registered"))   return "Diese E-Mail ist bereits registriert.";
  if (msg.includes("Password should be"))        return "Das Passwort muss mindestens 8 Zeichen lang sein.";
  if (msg.includes("rate limit"))                return "Zu viele Versuche. Bitte kurz warten.";
  return msg;
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ padding: "12px 16px", background: Z.errorContainer, borderRadius: 10, fontSize: 13, color: Z.error, lineHeight: 1.5 }}>
      {msg}
    </div>
  );
}

function SuccessBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ padding: "12px 16px", background: Z.primaryFixed, borderRadius: 10, fontSize: 13, color: Z.primary, lineHeight: 1.5, fontWeight: 500 }}>
      {msg}
    </div>
  );
}

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
      <div style={{ flex: 1, height: 1, background: Z.outlineVariant }} />
      <span style={{ fontSize: 12, color: Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>oder</span>
      <div style={{ flex: 1, height: 1, background: Z.outlineVariant }} />
    </div>
  );
}

// ── LOGIN ─────────────────────────────────────
function LoginView({ onGoogle, onLogin, onGoSignup, onGoReset }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Bitte E-Mail und Passwort eingeben."); return; }
    setLoading(true);
    try { await onLogin(email, password); }
    catch (err) { setError(mapError(err.message)); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <h2 style={{ fontSize: 26, fontWeight: 300, fontFamily: "'Manrope', sans-serif", color: Z.primary, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
          Willkommen zurück
        </h2>
        <p style={{ fontSize: 14, color: Z.onSurfaceVariant, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Melde dich an um fortzufahren.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <input type="email" placeholder="E-Mail-Adresse" value={email}
          onChange={(e) => setEmail(e.target.value)} style={inputStyle} autoComplete="email" />
        <input type="password" placeholder="Passwort" value={password}
          onChange={(e) => setPassword(e.target.value)} style={inputStyle} autoComplete="current-password" />
        <div style={{ textAlign: "right", marginTop: -4 }}>
          <button type="button" onClick={onGoReset} style={linkBtn}>Passwort vergessen?</button>
        </div>
        <ErrorBox msg={error} />
        <button type="submit" style={{ ...primaryBtn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
          {loading ? "Anmelden…" : "Anmelden"}
        </button>
      </form>

      <Divider />
      <button onClick={onGoogle} style={outlineBtn}><GoogleLogo /> Mit Google anmelden</button>

      <p style={{ textAlign: "center", fontSize: 13, color: Z.onSurfaceVariant, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        Noch kein Konto?{" "}
        <button onClick={onGoSignup} style={linkBtn}>Jetzt registrieren</button>
      </p>
    </div>
  );
}

// ── SIGNUP ────────────────────────────────────
function SignupView({ onGoogle, onSignup, onGoLogin }) {
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!name || !email || !password) { setError("Bitte alle Felder ausfüllen."); return; }
    if (password.length < 8) { setError("Passwort muss mindestens 8 Zeichen lang sein."); return; }
    if (password !== confirm) { setError("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try { await onSignup(email, password, name); setSuccess("Fast geschafft! Bitte bestätige deine E-Mail-Adresse."); }
    catch (err) { setError(mapError(err.message)); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <h2 style={{ fontSize: 26, fontWeight: 300, fontFamily: "'Manrope', sans-serif", color: Z.primary, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
          Konto erstellen
        </h2>
        <p style={{ fontSize: 14, color: Z.onSurfaceVariant, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Starte deine Routine — kostenlos.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <input type="text" placeholder="Dein Name" value={name}
          onChange={(e) => setName(e.target.value)} style={inputStyle} autoComplete="name" />
        <input type="email" placeholder="E-Mail-Adresse" value={email}
          onChange={(e) => setEmail(e.target.value)} style={inputStyle} autoComplete="email" />
        <input type="password" placeholder="Passwort (min. 8 Zeichen)" value={password}
          onChange={(e) => setPassword(e.target.value)} style={inputStyle} autoComplete="new-password" />
        <input type="password" placeholder="Passwort bestätigen" value={confirm}
          onChange={(e) => setConfirm(e.target.value)} style={inputStyle} autoComplete="new-password" />
        <ErrorBox msg={error} />
        <SuccessBox msg={success} />
        {!success && (
          <button type="submit" style={{ ...primaryBtn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
            {loading ? "Registrieren…" : "Konto erstellen"}
          </button>
        )}
      </form>

      {!success && <><Divider /><button onClick={onGoogle} style={outlineBtn}><GoogleLogo /> Mit Google registrieren</button></>}

      <p style={{ textAlign: "center", fontSize: 13, color: Z.onSurfaceVariant, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        Bereits ein Konto?{" "}
        <button onClick={onGoLogin} style={linkBtn}>Anmelden</button>
      </p>
    </div>
  );
}

// ── RESET ─────────────────────────────────────
function ResetView({ onReset, onGoLogin }) {
  const [email, setEmail]     = useState("");
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!email) { setError("Bitte E-Mail-Adresse eingeben."); return; }
    setLoading(true);
    try { await onReset(email); setSuccess(`Link gesendet an ${email}. Bitte prüfe deinen Posteingang.`); }
    catch (err) { setError(mapError(err.message)); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <h2 style={{ fontSize: 26, fontWeight: 300, fontFamily: "'Manrope', sans-serif", color: Z.primary, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
          Passwort zurücksetzen
        </h2>
        <p style={{ fontSize: 14, color: Z.onSurfaceVariant, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Wir schicken dir einen Reset-Link.
        </p>
      </div>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <input type="email" placeholder="E-Mail-Adresse" value={email}
          onChange={(e) => setEmail(e.target.value)} style={inputStyle} autoComplete="email" />
        <ErrorBox msg={error} />
        <SuccessBox msg={success} />
        {!success && (
          <button type="submit" style={{ ...primaryBtn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
            {loading ? "Senden…" : "Link senden"}
          </button>
        )}
      </form>
      <p style={{ textAlign: "center", fontSize: 13, color: Z.onSurfaceVariant, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <button onClick={onGoLogin} style={linkBtn}>← Zurück zur Anmeldung</button>
      </p>
    </div>
  );
}

// ── AUTH GATE (Haupt-Export) ──────────────────
export function AuthGate({ onGoogle, onLogin, onSignup, onReset }) {
  const [view, setView] = useState("login");
  const [legal, setLegal] = useState(null);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Guten Morgen" : hour < 18 ? "Guten Tag" : "Guten Abend";

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Plus Jakarta Sans', sans-serif; }
        input:focus { border-color: ${Z.primary} !important; box-shadow: 0 0 0 3px ${Z.primaryFixed}; }
      `}</style>

      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${Z.surface} 0%, ${Z.surfaceContainerLow} 50%, ${Z.secondaryContainer}44 100%)`,
        padding: "2rem",
      }}>
        <div style={{ maxWidth: 420, width: "100%" }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{
              width: 64, height: 64, borderRadius: 20,
              background: Z.primary,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", fontSize: 28,
            }}>
              🌿
            </div>
            <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 28, fontWeight: 300, color: Z.primary, letterSpacing: "-0.03em", marginBottom: 4 }}>
              Habit OS
            </h1>
            <p style={{ fontSize: 13, color: Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {greeting} — dein Routine-System wartet.
            </p>
          </div>

          {/* Card */}
          <div style={{
            background: Z.surfaceContainerLowest,
            borderRadius: 24,
            padding: "32px 28px",
            boxShadow: "0 40px 80px -20px rgba(85,98,77,0.12), 0 8px 24px -8px rgba(85,98,77,0.08)",
          }}>
            {view === "login"  && <LoginView  onGoogle={onGoogle} onLogin={onLogin} onGoSignup={() => setView("signup")} onGoReset={() => setView("reset")} />}
            {view === "signup" && <SignupView onGoogle={onGoogle} onSignup={onSignup} onGoLogin={() => setView("login")} />}
            {view === "reset"  && <ResetView  onReset={onReset}  onGoLogin={() => setView("login")} />}
          </div>

          <p style={{ textAlign: "center", fontSize: 11, color: Z.outline, marginTop: 20, lineHeight: 1.6, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Deine Daten werden sicher in der EU gespeichert.
            <br />
            <button onClick={() => setLegal("datenschutz")} style={{ ...linkBtn, fontSize: 11 }}>Datenschutz</button>
            {" · "}
            <button onClick={() => setLegal("impressum")} style={{ ...linkBtn, fontSize: 11 }}>Impressum</button>
          </p>
        </div>
      </div>
      {legal && <LegalSheet Z={Z} kind={legal} onClose={() => setLegal(null)} />}
    </>
  );
}
